import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { EmptyState } from '../components/ui/EmptyState';
import { pedidosApi } from '../services/api';
import { Loader2, Upload, Plus, FileText, RefreshCw, Search, ClipboardList } from 'lucide-react';

const ESTADO_META = {
  ocr_procesando:  { label: 'OCR en curso',   color: 'bg-amber-100 text-amber-700 border-amber-200' },
  revision_humana: { label: 'Revisión',       color: 'bg-blue-100 text-blue-700 border-blue-200' },
  confirmado:      { label: 'Confirmado',     color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  enviado_erp:     { label: 'Enviado ERP',    color: 'bg-violet-100 text-violet-700 border-violet-200' },
  error_ocr:       { label: 'Error OCR',      color: 'bg-rose-100 text-rose-700 border-rose-200' },
  archivado:       { label: 'Archivado',      color: 'bg-ink-100 text-ink-600 border-ink-200' },
};

const formatDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return '—'; }
};

const countLineasPendientes = (p) =>
  (p.lineas || []).filter((l) => l.estado === 'no_encontrado' || l.estado === 'sugerido').length;

export default function Pedidos() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [estado, setEstado] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const cargar = async () => {
    setLoading(true);
    try {
      const list = await pedidosApi.list({ q: q || undefined, estado: estado || undefined });
      setItems(list);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, []);

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      toast.info('Subiendo documento y ejecutando OCR (10-30 s)…');
      const p = await pedidosApi.upload(f);
      toast.success(`Pedido creado — ${p.lineas?.length || 0} líneas extraídas`);
      nav(`/pedidos/${p.id}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6" data-testid="pedidos-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-ink-900 font-display tracking-tight flex items-center gap-3">
            <ClipboardList className="w-8 h-8 text-brand-500" />
            Pedidos de cliente
          </h1>
          <p className="text-sm text-ink-500 mt-1">
            Recibe pedidos por PDF o email, la IA los lee, los cruza con el catálogo y genera la oferta.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf,image/*"
            onChange={onUpload}
            className="hidden"
            data-testid="pedido-file-input"
          />
          <Button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="btn-subir-pedido"
            className="bg-brand-500 hover:bg-brand-600 text-white"
          >
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Subir PDF / imagen
          </Button>
          <Button
            variant="outline"
            onClick={() => nav('/pedidos/nuevo')}
            data-testid="btn-pedido-manual"
          >
            <Plus className="w-4 h-4 mr-2" />
            Alta manual
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && cargar()}
            placeholder="Buscar por nº pedido, cliente o CIF…"
            className="pl-9"
            data-testid="pedido-buscar"
          />
        </div>
        <select
          value={estado}
          onChange={(e) => { setEstado(e.target.value); setTimeout(cargar, 50); }}
          className="border border-ink-200 rounded-md px-3 py-2 text-sm bg-white"
          data-testid="pedido-filtro-estado"
        >
          <option value="">Todos los estados</option>
          {Object.entries(ESTADO_META).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <Button variant="outline" onClick={cargar} data-testid="btn-pedido-recargar">
          <RefreshCw className="w-4 h-4 mr-2" />
          Buscar
        </Button>
      </div>

      {/* Lista */}
      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-ink-500 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={FileText}
            title="Aún no hay pedidos"
            description="Sube el primer PDF de pedido de cliente y la IA lo procesará."
            testid="pedidos-empty"
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="pedidos-tabla">
              <thead className="bg-ink-50 text-ink-600 uppercase text-xs">
                <tr>
                  <th className="text-left px-4 py-3">Nº pedido</th>
                  <th className="text-left px-4 py-3">Cliente</th>
                  <th className="text-left px-4 py-3">Líneas</th>
                  <th className="text-left px-4 py-3">Estado</th>
                  <th className="text-left px-4 py-3">Creado</th>
                  <th className="text-right px-4 py-3">Acción</th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => {
                  const meta = ESTADO_META[p.estado] || { label: p.estado, color: 'bg-ink-100 text-ink-700' };
                  const pend = countLineasPendientes(p);
                  return (
                    <tr key={p.id} className="border-t border-ink-100 hover:bg-ink-50/40" data-testid={`pedido-row-${p.id}`}>
                      <td className="px-4 py-3 font-medium text-ink-900">
                        {p.numero_pedido_cliente || <span className="text-ink-400 italic">(sin número)</span>}
                      </td>
                      <td className="px-4 py-3 text-ink-700">
                        {p.entidad_razon_social || <span className="text-ink-400 italic">—</span>}
                        {p.cif_detectado && <span className="text-xs text-ink-400 ml-2">{p.cif_detectado}</span>}
                      </td>
                      <td className="px-4 py-3 text-ink-700">
                        {p.lineas?.length || 0}
                        {pend > 0 && (
                          <Badge variant="outline" className="ml-2 text-[10px] border-amber-300 text-amber-700 bg-amber-50">
                            {pend} pendiente{pend > 1 ? 's' : ''}
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className={`text-xs ${meta.color}`}>{meta.label}</Badge>
                      </td>
                      <td className="px-4 py-3 text-ink-500 text-xs">{formatDate(p.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <Button size="sm" variant="outline" onClick={() => nav(`/pedidos/${p.id}`)} data-testid={`btn-abrir-${p.id}`}>
                          Abrir
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
